<template>
  <Dialog
    v-model:visible="showDialog"
    :header="t(header)"
    :modal="true"
    :draggable="false"
    :closable="true"
    :breakpoints="{ '960px': '90vw', '640px': '95vw' }"
    :style="{ width: '720px' }"
    class="domain-instructions-dialog"
    @update:visible="handleClose"
  >
    <template #header>
      <h3>{{ t(header) }}</h3>
    </template>
    <div
      v-if="type === 'authenticationInstructionNewMethod' || type === 'authenticationInstruction'"
      class="domain-instructions__body"
    >
      <div class="info-block">
        <i class="fa-solid fa-circle-info" />
        <p>{{ t('shop.my_domains.authentication.firstParagraph') }}</p>
      </div>

      <ol class="domain-instructions__steps">
        <li>
          <div
            class="domain-instructions__step-text"
            v-html="t('shop.my_domains.authentication.secondParagraph', [domainAuthSelector1, data.domain])"
          />
          <div class="domain-instructions__cname-row">
            <SpmKeyCard :secret-key="`${domainAuthSelector1}`" />
            <span class="font-semibold">IN CNAME</span>
            <SpmKeyCard :secret-key="`${domainAuthKey1}`" />
          </div>
        </li>
        <li>
          <div
            class="domain-instructions__step-text"
            v-html="t('shop.my_domains.authentication.secondParagraph', [domainAuthSelector2, data.domain])"
          />
          <div class="domain-instructions__cname-row">
            <SpmKeyCard :secret-key="`${domainAuthSelector2}`" />
            <span class="font-semibold">IN CNAME</span>
            <SpmKeyCard :secret-key="`${domainAuthKey2}`" />
          </div>
        </li>
      </ol>
    </div>
    <div
      v-else-if="type ==='domainValidationForm'"
      class="grid"
      style="padding: 1rem;text-align: left"
    >
      <div
        class="alert-info"
        v-html="t('shop.my_domains.validation.firstParagraph', [data.domain])"
      />
      <div class="col-12">
        <div class="flex-auto">
          <label
            for="confirmDomain"
            class="font-bold block mb-2"
          >{{ t('shop.my_domains.validation.secondParagraph') }} </label>
          <span>
            <InputText
              v-model="domainToConfirm"
              input-id="confirmDomain"
            />
            @{{ data.domain }}
          </span>
        </div>
      </div>
      <div
        v-if="emptyDomain"
        class="col-12"
      >
        <small class="p-error">{{ t('shop.my_domains.validation.emptyDomainError') }}</small>
      </div>
    </div>
    <div
      v-else-if="type ==='domainValidationDnsForm'"
      class="grid"
      style="padding: 1rem;text-align: left"
    >
      <div v-html="t('shop.my_domains.dnsValidation', [data.domain])" />
      <SpmKeyCard
        :secret-key="`${dnsValidationRecordsKey} ${data.dns_verification_key}`"
        class="mt-3"
      />
    </div>
    <div
      v-else-if="type ==='trackingDomainInstruction'"
      class="domain-instructions__body"
    >
      <div class="info-block">
        <i class="fa-solid fa-circle-info" />
        <div
          class="info-block__text"
          v-html="t('shop.my_domains.tracking.firstParagraph', [data.domain])"
        />
      </div>

      <div class="domain-instructions__form-group">
        <label class="domain-instructions__label">
          {{ t('shop.my_domains.subdomainCheckLabel') }}
        </label>
        <div class="domain-instructions__inline-form">
          <AutoComplete
            v-model="trackingDomain"
            type="text"
            class="form-control"
            :auto-highlight="true"
            :suggestions="suggestions"
            @complete="search"
          />
          <span class="domain-instructions__suffix">.{{ data.domain }}</span>
          <SpmButton
            :label="t('shop.my_domains.tracking.btnText')"
            class-style="p-button p-button-success"
            @click="verifyDomainValidForTracking"
          />
        </div>
        <div
          v-if="showText"
          class="domain-instructions__verify-result"
        >
          {{ t('shop.my_domains.tracking.verificationText') }}
          <span :style="styleText">{{ t(verifyText) }}</span>
        </div>
      </div>

      <template v-if="showDefaultInput">
        <div
          class="domain-instructions__step-text"
          v-html="t('shop.my_domains.tracking.secondParagraph')"
        />
        <div class="domain-instructions__cname-row">
          <SpmKeyCard :secret-key="`${trackingDomain}`" />
          <span class="font-semibold">IN CNAME</span>
          <SpmKeyCard :secret-key="`${redirectConfigDomain}`" />
        </div>
      </template>

      <div
        v-if="canToggleDefaultMedias"
        class="domain-instructions__checkbox-row"
      >
        <Checkbox
          id="trackingDomain"
          v-model="trackingDomainDefault"
          binary
        />
        <label for="trackingDomain">{{ t('shop.my_domains.tracking.footer') }}</label>
      </div>
    </div>
    <div
      v-else
      class="domain-instructions__body"
    >
      <div class="info-block">
        <i class="fa-solid fa-circle-info" />
        <div
          class="info-block__text"
          v-html="t('shop.my_domains.alignment.firstParagraph', [data.domain])"
        />
      </div>

      <div class="domain-instructions__form-group">
        <label class="domain-instructions__label">
          {{ t('shop.my_domains.subdomainCheckLabel') }}
        </label>
        <div class="domain-instructions__inline-form">
          <AutoComplete
            v-model="alignmentDomain"
            type="text"
            class="form-control"
            :auto-highlight="true"
            :suggestions="suggestions"
            @complete="search"
          />
          <span class="domain-instructions__suffix">.{{ data.domain }}</span>
          <SpmButton
            :label="t('shop.my_domains.alignment.footer')"
            class-style="p-button p-button-success"
            @click="verifyDomainForAlignment"
          />
        </div>
        <div
          v-if="showText"
          class="domain-instructions__verify-result"
        >
          {{ t('shop.my_domains.alignment.verificationText') }}
          <span :style="styleText">{{ t(verifyText) }}</span>
        </div>
      </div>

      <template v-if="showDefaultInput">
        <div
          class="domain-instructions__step-text"
          v-html="t('shop.my_domains.alignment.secondParagraph')"
        />
        <div class="domain-instructions__cname-row">
          <SpmKeyCard :secret-key="`${alignmentDomain}`" />
          <span class="font-semibold">IN CNAME</span>
          <SpmKeyCard :secret-key="`${domainConfigValue}`" />
        </div>
      </template>
    </div>
    <template #footer>
      <Button
        :label="t('close')"
        class="p-button p-button-secondary"
        @click="handleClose"
      />
      <Button
        v-if="type=== 'domainValidationForm'"
        :label="t('confirm')"
        class="p-button p-button-success"
        :disabled="disableValidate"
        @click="validateByEmail"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import Dialog from 'primevue/dialog';
import {
  computed, defineComponent, onMounted, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ShopsSendDomains,
  ShopsSendDomainsUpdateInput,
  ShopsSendDomainsUpdateInputItem,
} from '@/types/generated-types/graphql';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmKeyCard from '@/components/spm-primevue/SpmKeyCard.vue';
import {
  CheckDomainValidForAlignment,
  getShopsDomainsCount,
  MakeDomainDefault,
  ValidateDomainByEmail,
  CheckDomainValidForTracking,
} from '@/composables/shop/ShopsSendDomains';
import { showToastError, showToastSuccess } from '@/helpers';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import { UserState } from '@/composables/User';
import * as sha1 from 'sha1';

export default defineComponent({
  name: 'DomainInstructions',
  components: {
    Dialog,
    AutoComplete,
    SpmButton,
    Button,
    Checkbox,
    InputText,
    SpmKeyCard,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
      default: false,
    },

    data: {
      type: Object as PropType<ShopsSendDomains>,
      required: true,
    },

    type: {
      type: String,
      required: true,
    },

    locale: {
      type: String,
      required: true,
    },
  },

  emits: ['on-close-dialog', 'hide-dialog'],

  setup(props, context) {
    const { t } = useI18n();
    const showDialog = ref(props.displayDialog);
    const alignmentDomain = ref(props.data.alignment_domain);
    const trackingDomain = ref(props.data.tracking_domain);
    const verifyText = ref(t('shop.my_domains.alignment.notAvailable'));
    const styleText = ref('');
    const showText = ref(false);
    const showDefaultInput = ref(false);
    const domainConfigValue = process.env.VUE_APP_DOMAIN_CONFIG_VALUE;
    const domainAuthSelector1 = process.env.VUE_APP_DOMAIN_AUTH_SELECTOR1;
    const domainAuthSelector2 = process.env.VUE_APP_DOMAIN_AUTH_SELECTOR2;
    const domainAuthKey1 = process.env.VUE_APP_DOMAIN_AUTH_KEY1;
    const domainAuthKey2 = process.env.VUE_APP_DOMAIN_AUTH_KEY2;
    const trackingDomainDefault = ref(Boolean(props.data.tracking_domain_default_medias));
    const dialogWidth = ref('44vw');
    const shopsDomainCount = ref(0);
    const dnsValidationRecordsKey = process.env.VUE_APP_DNS_VALIDATION_RECORDS;
    const domainToConfirm = ref();
    const userId = UserState.user?.id;
    const header = ref();
    const validDomain = ref(false);
    const emptyDomain = ref(false);
    const disableValidate = ref(false);
    const redirectConfigDomain = process.env.VUE_APP_DOMAIN_LINKS_REDIRECT_CONFIGURATION_DOMAIN;
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);

    const subDomainsList = [
      'marketing',
      'email',
      'communication',
      'newsletter',
      'contact',
      'bounce',
      'information',
      'news',
      'info',
      'support',
      'emailing',
      'notice',
      'emails',
      'infos',
    ];

    switch (props.type) {
      case 'authenticationInstructionNewMethod':
      case 'authenticationInstruction':
        header.value = t('shop.my_domains.authentication.header');
        dialogWidth.value = '41vw';
        break;
      case 'alignmentInstruction':
        header.value = t('shop.my_domains.alignment.header');
        break;
      case 'trackingDomainInstruction':
        header.value = t('shop.my_domains.tracking.header');
        break;
      case 'domainValidationDnsForm':
      case 'domainValidationForm':
        header.value = t('shop.my_domains.validation.header');
        dialogWidth.value = '30vw';
        break;
      default:
        break;
    }

    if (props.type === 'authenticationInstructionNewMethod' || props.type === 'authenticationInstruction') {
      dialogWidth.value = '41vw';
    }

    const suggestions = ref<string[]>([]);

    const verifyDomainForAlignment = async () => {
      if (alignmentDomain.value) {
        // todo: why is id_shop optional??
        const {
          data,
          err,
        } = await CheckDomainValidForAlignment(props.data.id_shop ?? 0, props.data.id_domain, alignmentDomain.value);

        if (err) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        } else {
          verifyText.value = data ? t('shop.my_domains.alignment.available') : t('shop.my_domains.alignment.notAvailable');
          styleText.value = data ? 'font-weight: bold; color: green' : 'font-weight: bold; color: red';
          showText.value = true;
          showDefaultInput.value = !!data;
          validDomain.value = !!data;
        }
      }
    };

    const verifyDomainValidForTracking = async () => {
      if (trackingDomain.value) {
        // todo: why is id_shop optional??
        const {
          data,
          err,
        } = await CheckDomainValidForTracking(props.data.id_shop ?? 0, props.data.id_domain, trackingDomain.value);

        if (err) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
        } else {
          verifyText.value = data ? t('shop.my_domains.alignment.available') : t('shop.my_domains.alignment.notAvailable');
          styleText.value = data ? 'font-weight: bold; color: green' : 'font-weight: bold; color: red';
          showText.value = true;
          showDefaultInput.value = !!data;
          validDomain.value = !!data;
        }
      }
    };

    const validateByEmail = async () => {
      if (domainToConfirm.value === '' || domainToConfirm.value === undefined) {
        emptyDomain.value = true;
        setTimeout(() => {
          emptyDomain.value = false;
        }, 5000);
        return;
      }
      emptyDomain.value = false;
      disableValidate.value = true;
      const response = await ValidateDomainByEmail(idShop.value, props.data.id_domain, domainToConfirm.value);
      if (response && response.success) {
        await showToastSuccess(t('shop.my_domains.verify_success'));
        showDialog.value = false;
      } else await showToastError(t('shop.my_domains.saveError'));
      disableValidate.value = false;
      context.emit('hide-dialog');
    };

    const search = ({
      originalEvent,
      query,
    }: { originalEvent: InputEvent; query: string }) => {
      suggestions.value = subDomainsList.filter((subDomain) => subDomain.includes(query));
    };

    const canToggleDefaultMedias = computed(() => {
      const verified = props.data.tracking_domain_verified as unknown;
      const isVerified = verified === true || verified === 1 || verified === '1';
      return Boolean(props.data.tracking_domain) && isVerified;
    });

    const updateDefaultDomain = async (newValue: boolean) => {
      if (!props.data.id_shop) {
        return;
      }

      const shopsSendDomainInput: ShopsSendDomainsUpdateInputItem = {
        id_domain: props.data.id_domain,
        tracking_domain_default_medias: newValue,
        id_shop: idShop.value,
      };

      const result = await MakeDomainDefault([shopsSendDomainInput]);
      if (!result.status) {
        await showToastError('GENERIC_ERROR');
      }
    };

    watch(trackingDomainDefault, (newVal, oldVal) => {
      if (newVal === oldVal) return;
      updateDefaultDomain(Boolean(newVal));
    });

    const handleClose = async () => {
      if (validDomain.value) {
        context.emit('on-close-dialog', true);
      } else {
        context.emit('on-close-dialog', false);
      }
    };

    onMounted(async () => {
      shopsDomainCount.value = await getShopsDomainsCount(props.data.id_shop ?? 0);
    });

    return {
      t,
      showDialog,
      alignmentDomain,
      verifyDomainValidForTracking,
      verifyDomainForAlignment,
      verifyText,
      styleText,
      handleClose,
      showText,
      domainConfigValue,
      suggestions,
      search,
      trackingDomain,
      domainAuthSelector1,
      domainAuthSelector2,
      domainAuthKey1,
      domainAuthKey2,
      dialogWidth,
      updateDefaultDomain,
      domainToConfirm,
      dnsValidationRecordsKey,
      validateByEmail,
      header,
      showDefaultInput,
      trackingDomainDefault,
      emptyDomain,
      disableValidate,
      redirectConfigDomain,
      validDomain,
      canToggleDefaultMedias,
    };
  },
});
</script>

<style scoped lang="scss">
.alert-info {
  color: #EFAC06;
  background-color: #fde5ab;
  position: relative;
  padding: .75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: .25rem;
}

.domain-instructions__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0.5rem 0.25rem;
  text-align: left;
}

.info-block {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  background: linear-gradient(135deg, rgba(147, 197, 253, 0.12), rgba(148, 200, 64, 0.08));
  border-left: 3px solid rgba(76, 114, 178, 0.5);
  border-radius: 10px;
  padding: 0.85rem 1rem;
  color: #2e3e5c;

  i {
    color: #2f4a7f;
    font-size: 1rem;
    margin-top: 0.18rem;
    flex-shrink: 0;
  }

  p,
  .info-block__text {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.5;
    color: #2e3e5c;

    :deep(b) {
      color: #13233d;
    }

    :deep(br) {
      display: block;
      content: "";
      margin-top: 0.35rem;
    }
  }
}

.domain-instructions__steps {
  margin: 0;
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  li {
    color: #13233d;
  }
}

.domain-instructions__step-text {
  font-size: 0.92rem;
  color: #2e3e5c;
  margin-bottom: 0.55rem;

  :deep(b) {
    color: #13233d;
  }
}

.domain-instructions__cname-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: #f7f9fc;
  border: 1px dashed rgba(27, 42, 75, 0.15);
  border-radius: 10px;
}

.domain-instructions__form-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: #ffffff;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(27, 42, 75, 0.08);
  border-radius: 12px;
}

.domain-instructions__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2e3e5c;
}

.domain-instructions__inline-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;

  :deep(.p-autocomplete) {
    flex: 1 1 160px;
    min-width: 0;
  }

  :deep(.p-autocomplete-input) {
    width: 100%;
  }
}

.domain-instructions__suffix {
  color: #4a5b78;
  font-size: 0.92rem;
}

.domain-instructions__verify-result {
  font-size: 0.88rem;
  color: #2e3e5c;
}

.domain-instructions__checkbox-row {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  padding: 0.75rem 0.85rem;
  background: rgba(148, 200, 64, 0.08);
  border-radius: 10px;

  label {
    font-size: 0.88rem;
    line-height: 1.4;
    color: #2e3e5c;
  }
}
</style>
